/**
 * CSV → JSON Tool
 */

import * as Papa from 'papaparse';

import { SEMANTIC_TYPES } from '@/detectors/semanticTypes';
import { ToolError } from '@/engine/errors/ToolError';
import type { ITransformerTool } from '@/interfaces/ITool';

export const CsvToJsonTool: ITransformerTool = {
  id: 'csv-to-json',
  name: 'CSV → JSON',
  description: 'Convert CSV to JSON (array of objects or arrays)',
  version: '1.0.0',
  type: 'transformer',
  category: 'Converters',
  keywords: ['csv', 'json', 'convert', 'table', 'tabular'],

  supportedInputTypes: [SEMANTIC_TYPES.CSV],
  outputType: SEMANTIC_TYPES.JSON,

  attributes: [
    {
      name: 'hasHeader',
      type: 'boolean',
      label: 'Header Row',
      defaultValue: true,
      description: 'Treat first row as column headers',
    },
    {
      name: 'trimWhitespace',
      type: 'boolean',
      label: 'Trim Cells',
      defaultValue: true,
      description: 'Trim whitespace around cell values',
    },
  ],

  supportsInput(_input: string, detectedType?: string): boolean {
    return detectedType === 'CSV';
  },

  async transform(input: string, config: Record<string, unknown>): Promise<string> {
    try {
      input = input.replace(/^\uFEFF/, '');
      const hasHeader = config.hasHeader !== false;
      const trimWhitespace = config.trimWhitespace !== false;

      if (!hasHeader) {
        const result = Papa.parse<string[]>(input, {
          delimiter: ',',
          header: false,
          skipEmptyLines: true,
          transform: trimWhitespace ? (value) => value.trim() : undefined,
        });

        if (result.errors.length > 0) {
          throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
        }

        const data = (result.data ?? []).filter((row) => row.some((cell) => String(cell ?? '') !== ''));
        return JSON.stringify(data, null, 2);
      }

      const result = Papa.parse<Record<string, string>>(input, {
        delimiter: ',',
        header: true,
        skipEmptyLines: true,
        transformHeader: trimWhitespace ? (header) => header.trim() : undefined,
        transform: trimWhitespace ? (value) => value.trim() : undefined,
      });

      if (result.errors.length > 0) {
        throw ToolError.validation(result.errors[0]?.message ?? 'Invalid CSV');
      }

      const headerFields = result.meta.fields ?? [];
      if (headerFields.length === 0) return '[]';

      const data = (result.data ?? []).filter((row) => {
        return headerFields.some((key) => String(row[key] ?? '') !== '');
      });

      return JSON.stringify(data, null, 2);
    } catch (e) {
      if (e instanceof ToolError) throw e;
      const error = e as Error;
      throw ToolError.validation(`Invalid CSV: ${error.message}`, {
        suggestion: 'Check quotes and delimiters; ensure rows have consistent columns',
      });
    }
  },
};
